import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import QuestionCircle from "../Icons/QuestionCircle";

interface TooltipProps {
  text: string;
  /** Multi-line copy with max width. Defaults to true. */
  wrap?: boolean;
}

const TOOLTIP_MAX_WIDTH_PX = 288;
const VIEWPORT_PADDING_PX = 8;

const NewTooltip: React.FC<TooltipProps> = ({ text, wrap = true }) => {
  const { isStaffMode } = useStaffMode();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const tooltipWidth = Math.min(
      TOOLTIP_MAX_WIDTH_PX,
      window.innerWidth - VIEWPORT_PADDING_PX * 2,
    );
    const halfWidth = tooltipWidth / 2;

    let left = rect.left + rect.width / 2;
    left = Math.max(VIEWPORT_PADDING_PX + halfWidth, left);
    left = Math.min(window.innerWidth - VIEWPORT_PADDING_PX - halfWidth, left);

    setPosition({
      top: rect.top - 8,
      left,
    });
  }, []);

  const showTooltip = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hideTooltip = useCallback(() => {
    setVisible(false);
  }, []);

  const iconSizeClasses = isStaffMode
    ? "w-4 h-4 md:w-5 md:h-5 bg-secondary-dark"
    : "w-5 h-5 md:w-6 md:h-6 bg-primary-dark";
  const textSizeClasses = isStaffMode
    ? "text-xs md:text-sm"
    : "text-sm md:text-base";
  const contentClasses = wrap
    ? "max-w-xs whitespace-normal text-left leading-snug"
    : "whitespace-nowrap";

  return (
    <>
      <div
        ref={anchorRef}
        className="inline-flex items-center justify-center"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        <div
          className={`p-1 rounded-full ${iconSizeClasses} flex items-center justify-center cursor-help text-white`}
        >
          <QuestionCircle />
        </div>
      </div>

      {visible &&
        createPortal(
          <div
            className={`pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full bg-gray-font text-neutral ${textSizeClasses} font-normal px-2 py-1.5 rounded shadow-lg ${contentClasses}`}
            style={{
              top: position.top,
              left: position.left,
              maxWidth: TOOLTIP_MAX_WIDTH_PX,
            }}
            role="tooltip"
          >
            {text}
            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-font" />
          </div>,
          document.body,
        )}
    </>
  );
};

export default NewTooltip;
